import { useAuth } from "@/auth/Auth";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { addDays, addYears, format, isSameDay, isToday } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import * as React from "react";
import { DateRange } from "react-day-picker";
import { useTranslation } from "react-i18next";

interface DateRangePickerProps {
  className?: string;
  widthFull?: boolean;
  onChange?: (range: DateRange | undefined) => void;
  direction?: "past" | "future" | "both";
  defaultValue?: DateRange;
  /** When true, an omitted defaultValue stays empty instead of today. */
  allowEmpty?: boolean;
}

export default function DateRangePicker({
  className,
  widthFull,
  onChange,
  direction = "past",
  defaultValue,
  allowEmpty = false,
}: DateRangePickerProps) {
  const { t } = useTranslation("dashboard");
  const { authData } = useAuth();

  const [date, setDate] = React.useState<DateRange | undefined>(
    defaultValue ?? (allowEmpty ? undefined : {
      from: new Date(),
      to: new Date(),
    })
  );

  React.useEffect(() => {
    if (defaultValue) {
      setDate(defaultValue);
    }
  }, [defaultValue]);

  const today = new Date();

  const handlePresetChange = (value: string) => {
    const presetMap: Record<string, DateRange> = {
      yesterday: { from: addDays(today, -1), to: addDays(today, -1) },
      today: { from: today, to: today },
      "7days": { from: addDays(today, -7), to: today },
      "30days": { from: addDays(today, -30), to: today },
      "90days": { from: addDays(today, -90), to: today },

      allPast: {
        from: authData?.created_at
          ? new Date(authData.created_at)
          : addDays(today, -365),
        to: today,
      },

      tomorrow: { from: addDays(today, 1), to: addDays(today, 1) },
      next7: { from: today, to: addDays(today, 7) },
      next30: { from: today, to: addDays(today, 30) },
      next90: { from: today, to: addDays(today, 90) },
      nextYear: { from: today, to: addYears(today, 1) },
      allFuture: {
        from: today,
        to: addYears(today, 10),
      },
    };

    const newRange = presetMap[value];
    setDate(newRange);
    onChange?.(newRange);
  };

  const formatDateRange = (range: DateRange) => {
    if (!range.from) return t("datePicker.selectPeriod");

    if (range.to && isSameDay(range.from, range.to)) {
      if (isToday(range.from)) return t("datePicker.today");
      if (isSameDay(range.from, addDays(today, -1)))
        return t("datePicker.yesterday");
      if (isSameDay(range.from, addDays(today, 1)))
        return t("datePicker.tomorrow");
    }

    if (!range.to) {
      return format(range.from, "dd/MM/yyyy", { locale: ptBR });
    }

    return `${format(range.from, "dd/MM/yyyy", { locale: ptBR })} - ${format(
      range.to,
      "dd/MM/yyyy",
      { locale: ptBR }
    )}`;
  };

  const minDate =
    direction === "future"
      ? today
      : authData?.created_at
      ? new Date(authData.created_at)
      : undefined;
  const maxDate = direction === "past" ? today : addYears(today, 10);

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={"outline"}
            className={cn(
              "w-[max-content] justify-start text-left font-normal",
              !date && "text-muted-foreground",
              widthFull && "w-full"
            )}
            data-cy="date-range-picker-button"
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {date ? formatDateRange(date) : t("datePicker.selectPeriod")}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-3" align="start">
          <Select onValueChange={handlePresetChange}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder={t("datePicker.selectPeriod")} />
            </SelectTrigger>
            <SelectContent>
              {(direction === "past" || direction === "both") && (
                <>
                  <SelectItem value="yesterday">
                    {t("datePicker.yesterday")}
                  </SelectItem>
                  <SelectItem value="today">{t("datePicker.today")}</SelectItem>
                  <SelectItem value="7days">
                    {t("datePicker.last7Days")}
                  </SelectItem>
                  <SelectItem value="30days">
                    {t("datePicker.last30Days")}
                  </SelectItem>
                  <SelectItem value="90days">
                    {t("datePicker.last90Days")}
                  </SelectItem>
                  <SelectItem value="allPast">
                    {t("datePicker.allTime")}
                  </SelectItem>
                </>
              )}
              {(direction === "future" || direction === "both") && (
                <>
                  <SelectItem value="tomorrow">
                    {t("datePicker.tomorrow")}
                  </SelectItem>
                  <SelectItem value="next7">
                    {t("datePicker.next7Days")}
                  </SelectItem>
                  <SelectItem value="next30">
                    {t("datePicker.next30Days")}
                  </SelectItem>
                  <SelectItem value="next90">
                    {t("datePicker.next90Days")}
                  </SelectItem>
                  <SelectItem value="nextYear">
                    {t("datePicker.nextYear")}
                  </SelectItem>
                  <SelectItem value="allFuture">
                    {t("datePicker.allTimeFuture")}
                  </SelectItem>
                </>
              )}
            </SelectContent>
          </Select>

          <Calendar
            initialFocus
            mode="range"
            defaultMonth={date?.from}
            selected={date}
            onSelect={(newDate) => {
              setDate(newDate);
              onChange?.(newDate);
            }}
            numberOfMonths={2}
            locale={ptBR}
            disabled={{
              before: minDate,
              after: maxDate,
            }}
            modifiersStyles={{
              today:
                date?.from &&
                date?.to &&
                isSameDay(date.from, today) &&
                isSameDay(date.to, today)
                  ? undefined
                  : date &&
                    ((date.from && isSameDay(date.from, today)) ||
                      (date.to && isSameDay(date.to, today)))
                  ? undefined
                  : { border: "none", backgroundColor: "transparent" },
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
